import React, { useMemo } from 'react';
import { archetypeShortLabel } from '../../data/agentArchetypes';

/* The approved palette. Neutral for calls that stayed inside the boundary, amber
   for ones that reached something sensitive, indigo for refusals — so the two
   that matter read against a quiet background rather than competing with it. */
const INSIDE = 'var(--p7-line-strong, #D8DCE3)';
const REACHED = '#C7881B';
const REFUSED = '#5A51E0';

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-10-22` → `Oct 22`, without pulling in a date library for one label. */
function dayLabel(iso) {
  const [, m, d] = String(iso || '').split('-');
  const month = MONTH[Number(m) - 1];
  return month ? `${month} ${Number(d)}` : String(iso || '');
}

/**
 * What the estate has been doing, at a glance.
 *
 * <h3>Days with nothing in them are not days of nothing</h3>
 *
 * An agent that has never run sends no days at all, and the chart shows only
 * the days the estate actually ran. A flat line at zero would say the estate
 * was asked and did nothing, which is a different claim from not having been
 * asked — and the wrong one for a new agent.
 *
 * <h3>Why ownership is on this screen</h3>
 *
 * An agent nobody answers for cannot go execution ready — the readiness gate
 * checks for a consenting owner — so "unowned" is not a label, it is the one
 * thing on this page somebody can act on. It carries its action beside it.
 */
export default function ProtectDashboard({
  rows = [], onProtectAnother, onOpen, onNominate,
  /* A build this person left half-finished, if there is one. Server-side it is
     scoped to them in the tenant they are signed in to, so nobody is ever
     offered somebody else's unfinished agent. */
  resume = null, onResume, onDiscardResume,
}) {
  const { days, max, refused, reached, runs, byArchetype, windowDays } = useMemo(() => {
    const byDay = new Map();
    const kinds = new Map();
    rows.forEach((r) => {
      if (r.archetype) kinds.set(r.archetype, (kinds.get(r.archetype) || 0) + 1);
      (r.exposure?.days || []).forEach((d) => {
        const cur = byDay.get(d.day) || { day: d.day, runs: 0, reached: 0, refused: 0 };
        cur.runs += d.runs; cur.reached += d.reached; cur.refused += d.refused;
        byDay.set(d.day, cur);
      });
    });
    const list = [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
    return {
      days: list,
      max: Math.max(1, ...list.map((d) => d.runs)),
      refused: list.reduce((n, d) => n + d.refused, 0),
      reached: list.reduce((n, d) => n + d.reached, 0),
      runs: list.reduce((n, d) => n + d.runs, 0),
      byArchetype: [...kinds.entries()],
      /* The window the backend measured, not a number written here. */
      windowDays: rows.find((r) => r.exposure?.windowDays)?.exposure?.windowDays || null,
    };
  }, [rows]);

  /* Of the calls it made, how many touched something sensitive. Zero runs is
     not zero percent — it is no answer, and the counter says so. */
  const reachedPct = runs > 0 ? `${Math.round((reached / runs) * 100)}%` : '—';

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                    flexWrap: 'wrap', marginBottom: 'var(--p7-space-5)' }}>
        <h1 style={{ margin: 0, flex: 1, font: 'var(--p7-text-display)',
                     fontFamily: 'var(--p7-font-sans)', letterSpacing: '-.02em' }}>
          Your agents
        </h1>
        {windowDays ? <span className="p7-status">Last {windowDays} days</span> : null}
        <button type="button" className="p7-btn p7-btn--primary" onClick={onProtectAnother}>
          Protect another
        </button>
      </div>

      {/* Offered, never resumed on their behalf — they may have come here to do
          something else entirely. */}
      {resume && onResume ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-3)',
                      flexWrap: 'wrap', marginBottom: 'var(--p7-space-5)',
                      padding: 'var(--p7-space-3) var(--p7-space-4)',
                      border: '1px solid var(--p7-line)',
                      borderRadius: 'var(--p7-radius, 8px)',
                      background: 'var(--p7-surface-sunken)' }}>
          <span style={{ flex: 1, minWidth: 0, font: 'var(--p7-text-ui)',
                         fontFamily: 'var(--p7-font-sans)', color: 'var(--p7-ink)' }}>
            You left <strong>{resume.tile?.name || 'a build'}</strong> half-finished.
            Nothing has been created yet.
          </span>
          {onDiscardResume ? (
            <button type="button" className="p7-btn p7-btn--sm" onClick={onDiscardResume}>
              Discard
            </button>
          ) : null}
          <button type="button" className="p7-btn p7-btn--sm p7-btn--primary"
                  onClick={() => onResume(resume)}>
            Continue where you left off
          </button>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--p7-space-6)', flexWrap: 'wrap',
                    marginBottom: 'var(--p7-space-5)' }}>
        <Kpi id="protected" value={String(rows.length)} label="Agents protected" />
        <Kpi id="archetype" label="By archetype" value={byArchetype.length === 0 ? '—' : null}>
          {byArchetype.map(([kind, n], i) => (
            <span key={kind}>
              {i > 0 ? <span style={{ color: 'var(--p7-ink-faint)' }}> · </span> : null}
              {n}
              <i style={{ fontStyle: 'normal', fontWeight: 400, fontSize: '15px',
                          color: 'var(--p7-ink-subtle)' }}> {archetypeShortLabel(kind)}</i>
            </span>
          ))}
        </Kpi>
        <Kpi id="refused" value={String(refused)} label="Refused"
             tone={refused > 0 ? REFUSED : null} />
        <Kpi id="reached" value={reachedPct} label="Reached sensitive data"
             tone={reached > 0 ? REACHED : null} />
      </div>

      {days.length > 0 ? (
        <div className="p7-card" style={{ padding: 'var(--p7-space-4)',
                                          marginBottom: 'var(--p7-space-5)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                        flexWrap: 'wrap', marginBottom: 'var(--p7-space-4)' }}>
            <h2 className="p7-eyebrow" style={{ margin: 0 }}>Protected calls</h2>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <Key swatch={INSIDE} label="Inside boundary" />
              <Key swatch={REACHED} label="Reached sensitive" />
              <Key swatch={REFUSED} label="Refused" />
            </div>
          </div>
          <div data-testid="plot"
               style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '150px',
                        paddingTop: '16px' }}>
            {days.map((d) => <Column key={d.day} d={d} max={max} />)}
          </div>
          <div data-testid="xaxis"
               style={{ display: 'flex', gap: '6px', marginTop: '7px' }}>
            {days.map((d, i) => (
              <span key={d.day} style={{ flex: 1, minWidth: 0, textAlign: 'center',
                                         font: 'var(--p7-text-caption)',
                                         fontFamily: 'var(--p7-font-mono)',
                                         color: 'var(--p7-ink-faint)' }}>
                {i % 3 === 0 ? dayLabel(d.day) : ''}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      <h2 className="p7-eyebrow" style={{ margin: '0 0 var(--p7-space-3)' }}>By agent</h2>
      <div className="p7-card" style={{ padding: 0 }}>
        {rows.map((r) => (
          <AgentRow key={r.agentId} r={r} onOpen={onOpen} onNominate={onNominate} />
        ))}
      </div>
    </div>
  );
}

function Kpi({ id, value, muted, label, tone, children }) {
  return (
    <div data-testid={`kpi-${id}`}>
      <div style={{ font: '700 26px/32px var(--p7-font-sans)', letterSpacing: '-.02em',
                    fontVariantNumeric: 'tabular-nums', color: tone || 'var(--p7-ink)' }}>
        {children || value}
        {muted ? <span style={{ color: 'var(--p7-ink-faint)', fontWeight: 400,
                                fontSize: '18px' }}>{muted}</span> : null}
      </div>
      <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                    color: 'var(--p7-ink-subtle)' }}>{label}</div>
    </div>
  );
}

function Key({ swatch, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px',
                   font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                   color: 'var(--p7-ink-muted)' }}>
      <span aria-hidden="true" style={{ width: '9px', height: '9px', borderRadius: '2px',
                                        background: swatch }} />
      {label}
    </span>
  );
}

function Column({ d, max }) {
  const insideN = Math.max(0, d.runs - d.reached - d.refused);
  const seg = (v, c) => (v > 0
    ? <div style={{ height: `${(v / d.runs) * 100}%`, background: c }} /> : null);
  return (
    <div style={{ flex: 1, minWidth: '10px', height: '100%', display: 'flex',
                  flexDirection: 'column', justifyContent: 'flex-end', position: 'relative' }}>
      {/* The refusal count sits above its own column: it is the number somebody
          came to this screen to find, and hunting for it in a tooltip is work. */}
      {d.refused > 0 ? (
        <span data-testid="refused-count"
              style={{ position: 'absolute', top: '-15px', left: 0, right: 0,
                       textAlign: 'center', font: '600 11px/1 var(--p7-font-mono)',
                       color: REFUSED }}>
          {d.refused}
        </span>
      ) : null}
      <div
        role="img"
        aria-label={`${dayLabel(d.day)}: ${d.runs} calls, ${d.reached} reached sensitive, `
          + `${d.refused} refused`}
        title={`${dayLabel(d.day)} · ${d.runs} calls — ${d.reached} reached sensitive, `
          + `${d.refused} refused`}
        style={{ height: `${Math.round((d.runs / max) * 100)}%`, display: 'flex',
                 flexDirection: 'column-reverse', borderRadius: '4px', overflow: 'hidden' }}
      >
        {seg(insideN, INSIDE)}
        {seg(d.reached, REACHED)}
        {seg(d.refused, REFUSED)}
      </div>
    </div>
  );
}

/**
 * What this agent's ownership record means, and what clears it.
 *
 * Taken from `OwnershipState`'s own definitions, because two of these are easy
 * to swap and expensive to get wrong. ORPHANED is "owner determined
 * non-existent as a valid accountable human". RETIRED is terminal and sealed —
 * decommissioned, a declined nomination, or superseded by a newer record — so
 * it means nobody answers for the agent, not that somebody has vanished.
 *
 * ACTIVE and DELEGATED say nothing: the agent is owned, and a delegation does
 * not move accountability. A handover in flight is worth stating and carries no
 * action, because the owner remains accountable until it completes.
 */
/*
 * An action appears only where the platform can carry it out.
 *
 * `OwnershipConsentService.claimOwnership:86-90` reads the agent's live record
 * and refuses anything that is not DISCOVERED_UNOWNED with a 409, and
 * OwnershipController exposes no remind and no re-assign at all. Three of the
 * four buttons here could therefore only ever produce an error:
 *
 *   NOMINATED  → 'Remind'    — no endpoint nudges a pending owner
 *   ORPHANED   → 'Re-assign' — no endpoint, and this is the state that needs one most
 *   RETIRED    → 'Nominate'  — terminal; claim refuses it, and should
 *
 * Each still SAYS its state, because the fact is true and the person needs to
 * know it. What it no longer does is invite a click that cannot complete.
 */
const OWNERSHIP = {
  DISCOVERED_UNOWNED: { say: 'Unowned — nominate an owner', act: 'Nominate', tone: 'deny' },
  NOMINATED: { say: 'Awaiting owner acceptance', act: null, tone: 'hold' },
  ORPHANED: { say: 'Owner no longer exists', act: null, tone: 'deny' },
  RETIRED: { say: 'Ownership ended — nobody answers for it', act: null, tone: 'deny' },
  TRANSFER_PENDING: { say: 'Handover in flight', act: null, tone: 'hold' },
  SUCCESSION_PENDING: { say: 'Succession in flight', act: null, tone: 'hold' },
};

function AgentRow({ r, onOpen, onNominate }) {
  const e = r.exposure || {};
  const total = (e.runs || 0) || 1;
  const insideN = Math.max(0, (e.runs || 0) - (e.reached || 0) - (e.refused || 0));
  const ran = (e.runs || 0) > 0;
  const own = OWNERSHIP[r.ownership?.state] || null;

  return (
    <div data-testid={`agent-${r.agentId}`}
         role="button"
         tabIndex={0}
         onClick={() => onOpen && onOpen(r)}
         onKeyDown={(ev) => { if (ev.key === 'Enter') onOpen && onOpen(r); }}
         style={{ display: 'flex', alignItems: 'center', gap: 'var(--p7-space-4)',
                  padding: 'var(--p7-space-4)', borderTop: '1px solid var(--p7-line-soft)',
                  cursor: onOpen ? 'pointer' : 'default' }}>
      <div style={{ width: '210px', flex: 'none', minWidth: 0 }}>
        <div style={{ font: 'var(--p7-text-ui)', fontFamily: 'var(--p7-font-sans)',
                      fontWeight: 600 }}>{r.name}</div>
        <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                      color: 'var(--p7-ink-subtle)' }}>
          {[r.archetype ? archetypeShortLabel(r.archetype) : null,
            (r.reach || []).join(', ') || null].filter(Boolean).join(' · ') || r.phase}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        {own ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px',
                        marginBottom: ran ? '7px' : 0 }}>
            <span className={`p7-status p7-status--${own.tone}`}>{own.say}</span>
            {own.act ? (
              <button type="button" className="p7-btn p7-btn--sm"
                      onClick={(ev) => { ev.stopPropagation(); onNominate && onNominate(r); }}>
                {own.act}
              </button>
            ) : null}
          </div>
        ) : null}

        {ran ? (
          <>
            <div style={{ display: 'flex', height: '9px', gap: '2px', borderRadius: '3px',
                          overflow: 'hidden' }}>
              <span style={{ width: `${(insideN / total) * 100}%`, background: INSIDE }} />
              <span style={{ width: `${((e.reached || 0) / total) * 100}%`, background: REACHED }} />
              <span style={{ width: `${((e.refused || 0) / total) * 100}%`, background: REFUSED }} />
            </div>
            <div style={{ marginTop: '6px', display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
              <Key swatch={INSIDE} label={`Inside ${insideN}`} />
              <Key swatch={REACHED} label={`Reached ${e.reached || 0}`} />
              <Key swatch={REFUSED} label={`Refused ${e.refused || 0}`} />
            </div>
          </>
        ) : !own ? (
          <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                        color: 'var(--p7-ink-subtle)' }}>
            Nothing run yet. It can reach {(r.reach || []).join(', ') || 'nothing recorded'}.
          </div>
        ) : null}
      </div>

      <div style={{ width: '92px', flex: 'none', textAlign: 'right' }}>
        {r.score == null ? (
          <span style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                         color: 'var(--p7-ink-faint)' }}>Not yet scored</span>
        ) : (
          <>
            <div style={{ font: '700 19px/22px var(--p7-font-sans)',
                          color: 'var(--p7-hold-ink)' }}>{String(r.score)}</div>
            <div style={{ font: 'var(--p7-text-caption)', fontFamily: 'var(--p7-font-sans)',
                          color: 'var(--p7-ink-subtle)' }}>{r.band}</div>
          </>
        )}
      </div>

      <span aria-hidden="true" style={{ flex: 'none', color: 'var(--p7-ink-faint)',
                                        fontSize: '18px' }}>›</span>
    </div>
  );
}
